import React, { useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import styled from 'styled-components/macro';
import { Button, Spinner, StatusPill } from '@united-talent-agency/components';
import { DEFAULT_EXCLUDED_STATUSES } from '../../utils/build-export-query';

const MODAL_STYLE = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    maxWidth: 420,
    width: '90%',
  },
  overlay: { zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.4)' },
};

// Mounted only while open (see Header), so useState initializers read the current saved
// settings on every open — no stale first-mount state, and mid-edit changes are never
// clobbered by an async settings update.
const ExportSettingsModal = ({
  isOpen,
  deskStatuses = [],
  settings,
  defaultHideFutureCalls = false,
  title,
  confirmLabel,
  loading = false,
  exporting = false,
  onConfirm,
  onCancel,
}) => {
  const [excluded, setExcluded] = useState(
    () => new Set(settings?.excludedStatuses ?? DEFAULT_EXCLUDED_STATUSES)
  );
  // With nothing saved for this desk yet, inherit the desk-wide setting; otherwise turning on
  // per-export control would silently start exporting future calls for desks that hide them.
  const [hideFutureCalls, setHideFutureCalls] = useState(
    settings?.hideFutureCalls ?? defaultHideFutureCalls
  );

  const allExcluded = useMemo(
    () => deskStatuses.length > 0 && deskStatuses.every((s) => excluded.has(s.status)),
    [deskStatuses, excluded]
  );

  const disabled = loading || exporting;

  const toggleStatus = (code) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    if (exporting) {
      return;
    }
    onConfirm({ excludedStatuses: Array.from(excluded), hideFutureCalls });
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={exporting ? undefined : onCancel}
      style={MODAL_STYLE}
    >
      <h3>{title}</h3>
      {loading && <p data-cy="export-settings-loading">Loading saved settings…</p>}
      <fieldset disabled={disabled} style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend>Status groups to include</legend>
        {deskStatuses.map((s) => (
          <label
            key={s.status}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}
          >
            <input
              type="checkbox"
              data-status={s.status}
              checked={!excluded.has(s.status)}
              onChange={() => toggleStatus(s.status)}
            />
            {/* StatusPill's container is width:100% + centered, so it must be boxed to a
                fixed width or it eats the row and pushes the name to the right edge. */}
            <span style={{ width: 44, flex: 'none' }}>
              <StatusPill status={s.status} color={s.color} />
            </span>
            <span>{s.name || s.status}</span>
          </label>
        ))}
      </fieldset>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
        <input
          type="checkbox"
          data-cy="export-settings-hide-future"
          checked={hideFutureCalls}
          disabled={disabled}
          onChange={(e) => setHideFutureCalls(e.target.checked)}
        />
        <span>Hide future calls</span>
      </label>
      <ButtonContainer>
        <ActionButton
          data-cy="export-settings-cancel"
          tabIndex={0}
          small
          primary={false}
          disabled={exporting}
          onClick={onCancel}
          text="Cancel"
        />
        <ActionButton
          data-cy="export-settings-confirm"
          tabIndex={0}
          small
          onClick={handleConfirm}
          disabled={disabled || allExcluded}
          data-loading={exporting}
          text={exporting ? <Spinner size={15} /> : confirmLabel}
        />
      </ButtonContainer>
    </ReactModal>
  );
};

const ButtonContainer = styled.div({
  marginTop: 16,
  justifyContent: 'flex-end',
  display: 'flex',
  gap: 8,
  position: 'relative',
});

const ActionButton = styled(Button)({
  transition: 'all 350ms',
  // Focus
  ':focus': {
    border: '1px solid rgb(33, 135, 185)',
  },
  // Loading
  '&[data-loading="true"]': {
    backgroundColor: 'transparent',
  },
  '&[data-loading="true"]:hover': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  '&[data-loading="true"] > span': {
    display: 'flex',
    alignItems: 'center',
  },
});

export default ExportSettingsModal;
